import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Breadcrumb, Button, Card, Checkbox, Col, Input, message, Row, Space, Spin, Switch, Tag, Typography } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import ApiService from '../../../../../network/ApiService';
import { ADMIN_PERMISSION_MODULES } from '../../../../../network/adminPermissions';

const sorted = values => [...new Set(values)].sort();

export default function UserDetail() {
  const [user, setUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { id } = useParams();
  const history = useHistory();
  const api = useMemo(() => new ApiService(), []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [userResponse, permissionResponse] = await Promise.all([
          api.get(`admin/users/${id}`), api.get(`admin/users/${id}/permissions`),
        ]);
        if (!active) return;
        setUser(userResponse); setOriginalUser(userResponse);
        setPermissions(sorted(permissionResponse.permissions || []));
        setOriginalPermissions(sorted(permissionResponse.permissions || []));
      } catch (error) { message.error(error.message || 'Benutzer konnte nicht geladen werden.'); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [api, id]);

  const userChanged = user && originalUser && JSON.stringify(user) !== JSON.stringify(originalUser);
  const permissionsChanged = JSON.stringify(sorted(permissions)) !== JSON.stringify(sorted(originalPermissions));
  const setPermission = (permission, enabled) => setPermissions(current => sorted(enabled ? [...current, permission] : current.filter(value => value !== permission)));
  const setModule = (module, enabled) => {
    const values = module.permissions.map(([permission]) => permission);
    setPermissions(current => sorted(enabled ? [...current, ...values] : current.filter(value => !values.includes(value))));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (userChanged) {
        const updated = await api.patch(`admin/users/${id}`, { firstName: user.first, lastName: user.last, email: user.email, tel: user.tel || '', verified: user.verified, type: user.type });
        setUser(updated); setOriginalUser(updated);
      }
      if (permissionsChanged && user.type === 'admin') {
        const updated = await api.put(`admin/users/${id}/permissions`, { permissions: sorted(permissions) });
        const next = sorted(updated.permissions || []); setPermissions(next); setOriginalPermissions(next);
      }
      message.success('Benutzer und Berechtigungen wurden gespeichert.');
    } catch (error) { message.error(error.message || 'Änderungen konnten nicht gespeichert werden.'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`admin/users/${id}`); message.success('Benutzer wurde gelöscht.'); history.push('/admin/user'); }
    catch (error) { message.error(error.message || 'Benutzer konnte nicht gelöscht werden.'); }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;
  if (!user) return <Alert type="error" message="Benutzer nicht verfügbar" showIcon />;

  return <div style={{ padding: 24 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <Breadcrumb><Breadcrumb.Item><a href="/admin/">Administration</a></Breadcrumb.Item><Breadcrumb.Item><a href="/admin/user">Admin-Benutzer</a></Breadcrumb.Item><Breadcrumb.Item>{user.first} {user.last}</Breadcrumb.Item></Breadcrumb>
      <Space><Button type="primary" onClick={save} loading={saving} disabled={!userChanged && !permissionsChanged}>Änderungen speichern</Button><Button danger onClick={remove}>Benutzer löschen</Button></Space>
    </div>
    <Card title="Benutzerdaten" style={{ marginTop: 16 }}><Row gutter={[16, 16]}>
      <Col xs={24} md={12}><Typography.Text>Vorname</Typography.Text><Input value={user.first} onChange={event => setUser({ ...user, first: event.target.value })} /></Col>
      <Col xs={24} md={12}><Typography.Text>Familienname</Typography.Text><Input value={user.last} onChange={event => setUser({ ...user, last: event.target.value })} /></Col>
      <Col xs={24} md={12}><Typography.Text>E-Mail</Typography.Text><Input value={user.email} onChange={event => setUser({ ...user, email: event.target.value })} /></Col>
      <Col xs={24} md={12}><Typography.Text>Telefon</Typography.Text><Input value={user.tel || ''} onChange={event => setUser({ ...user, tel: event.target.value })} /></Col>
      <Col xs={24} md={12}><Space><Typography.Text>Verifiziert</Typography.Text><Switch checked={Boolean(user.verified)} onChange={verified => setUser({ ...user, verified })} /></Space></Col>
      <Col xs={24} md={12}><Typography.Text>Typ</Typography.Text><div><Tag color={user.type === 'admin' ? 'blue' : 'default'}>{user.type}</Tag></div></Col>
    </Row></Card>
    {user.type === 'admin' ? <Card title="Admin-Berechtigungen" style={{ marginTop: 16 }} extra={<Tag color="blue">{permissions.length} aktiv</Tag>}>
      <Alert type="info" showIcon message="Die Module entsprechen den Bereichen auf der Admin-Startseite. Einzelne Aktionen können separat freigegeben werden." style={{ marginBottom: 16 }} />
      <Row gutter={[16, 16]}>{ADMIN_PERMISSION_MODULES.map(module => {
        const values = module.permissions.map(([permission]) => permission);
        const selected = values.filter(permission => permissions.includes(permission)).length;
        return <Col xs={24} lg={12} key={module.key}><Card size="small" title={module.title} extra={<Checkbox checked={selected === values.length} indeterminate={selected > 0 && selected < values.length} onChange={event => setModule(module, event.target.checked)}>Ganzes Modul</Checkbox>} style={{ height: '100%' }}>
          <Typography.Paragraph type="secondary">{module.description}</Typography.Paragraph><Space direction="vertical">{module.permissions.map(([permission, label]) => <Checkbox key={permission} checked={permissions.includes(permission)} onChange={event => setPermission(permission, event.target.checked)}>{label}</Checkbox>)}</Space>
        </Card></Col>;
      })}</Row>
    </Card> : <Alert type="warning" showIcon style={{ marginTop: 16 }} message="Berechtigungen gelten ausschließlich für Admin-Benutzer." />}
  </div>;
}
